import React, { type FunctionComponent, useCallback, useMemo } from 'react';

import { ErrorScreen } from './ErrorScreen';
import { LoginPage } from './LoginPage';
import { StorageAccessPage } from './StorageAccessPage';
import { type UrlProps, useUrl } from './useUrl';

import { useValidationState, Subjects, LoadingStatus } from './useValidationState';
import { type NavigationPolicy } from '../navigation-policy';

export type ChildrenProps = {
	handleIframeOnLoad: () => void;
	handleContentLoadStarted: () => void;
	navigationPolicy?: NavigationPolicy;
	handleIframeUnload: () => void;
	redirectedToLogin?: boolean;
};

export type Props = UrlProps & {
	navigationPolicy?: NavigationPolicy;
	children: FunctionComponent<ChildrenProps>;
};

const noop = () => {};

export const PageLoadValidation = (props: Props) => {
	const { standaloneConfluenceUrl, embeddedConfluenceUrl } = useUrl(props);

	const isEmbeddedUrlOnSameDomain = embeddedConfluenceUrl
		? window.location.origin === new URL(embeddedConfluenceUrl).origin
		: false;

	return isEmbeddedUrlOnSameDomain ? (
		props.children({
			handleIframeOnLoad: noop,
			handleContentLoadStarted: noop,
			handleIframeUnload: noop,
			navigationPolicy: props.navigationPolicy,
		})
	) : (
		<CrossDomainErrorValidation
			key={embeddedConfluenceUrl || props.contentId}
			standaloneConfluenceUrl={standaloneConfluenceUrl}
			embeddedConfluenceUrl={embeddedConfluenceUrl}
			allowedFeatures={props.allowedFeatures}
			{...props}
		/>
	);
};

const CrossDomainErrorValidation = ({
	allowedFeatures,
	children,
	contentId,
	embeddedConfluenceUrl,
	navigationPolicy,
	parentProduct,
	spaceKey,
	standaloneConfluenceUrl,
}: {
	allowedFeatures?: Props['allowedFeatures'];
	children: Props['children'];
	contentId: Props['contentId'];
	embeddedConfluenceUrl: ReturnType<typeof useUrl>['embeddedConfluenceUrl'];
	navigationPolicy?: NavigationPolicy;
	parentProduct: Props['parentProduct'];
	spaceKey?: Props['spaceKey'];
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
}) => {
	const { state, dispatch } = useValidationState();

	const {
		iframeLoaded,
		contentLoadStarted,
		shouldAskStorageAccess,
		shouldAskToLogin,
		redirectedToLogin,
		iframeUnloaded,
	} = state;

	const isLoginFlowDisabled = useMemo(
		() => allowedFeatures?.includes('disable-login-flow') ?? false,
		[allowedFeatures],
	);

	const reloadEP = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.RELOAD_EP,
		});
	}, [dispatch]);

	const failToLoad = useMemo(
		() =>
			(iframeLoaded && !contentLoadStarted) ||
			/*
      If anonymous access is permitted globally but restricted on a content's parent space,
      the iframe will attempt a login redirect which we intercept.
      To avoid an error in the iframe, we need to wait for the iframe window to unload
      before unmouting the Iframe component.
    */
			(iframeUnloaded && redirectedToLogin),
		[contentLoadStarted, iframeLoaded, iframeUnloaded, redirectedToLogin],
	);

	const pageNavigationPolicy = useMemo(
		(): NavigationPolicy => ({
			navigate(url, modifiers, defaultNavigate) {
				// modifiy the navigationPolicy to intercept login redirects
				// so that login can be handled outside of the iframe
				if (url.includes('/login?application=confluence')) {
					dispatch({
						subject: Subjects.CONTENT_LOAD,
						status: LoadingStatus.REDIRECTED_TO_LOGIN,
					});
					return;
				}
				return navigationPolicy?.navigate
					? navigationPolicy.navigate(url, modifiers, defaultNavigate)
					: defaultNavigate(url, modifiers);
			},
			shimUrl: navigationPolicy?.shimUrl,
			domainNamesToReplace: navigationPolicy?.domainNamesToReplace,
		}),
		[navigationPolicy, dispatch],
	);

	const handleIframeOnLoad = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.IFRAME_LOADED,
		});
	}, [dispatch]);

	const handleContentLoadStarted = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.CONTENT_LOAD_STARTED,
		});
	}, [dispatch]);

	const handleIframeUnload = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.IFRAME_UNLOADED,
		});
	}, [dispatch]);

	if (!failToLoad) {
		return children({
			handleIframeOnLoad,
			handleContentLoadStarted,
			handleIframeUnload,
			navigationPolicy: pageNavigationPolicy,
			redirectedToLogin,
		});
	}

	if (!isLoginFlowDisabled) {
		if (shouldAskStorageAccess) {
			return (
				<StorageAccessPage
					url={embeddedConfluenceUrl}
					contentId={contentId}
					spaceKey={spaceKey}
					parentProduct={parentProduct}
				/>
			);
		}
		if (shouldAskToLogin) {
			return (
				<LoginPage
					contentId={contentId}
					reloadEP={reloadEP}
					spaceKey={spaceKey}
					url={embeddedConfluenceUrl}
				/>
			);
		}
	}
	return <ErrorScreen standaloneConfluenceUrl={standaloneConfluenceUrl} />;
};
